/**
 * Acting-tenant selection for dual-licensed staff (single login, in-app switch).
 *
 * A dual-licensed agent holds one `users` row per tenant; the Auth0 token names
 * only the first-created row. Instead of a full Auth0 org re-login, the SPA
 * stores the chosen membership and sends `X-Acting-Tenant-Id` on every request
 * — the backend validates it against an ACTIVE agent_tenant_memberships row and
 * re-anchors the session to that tenant's users row (403 otherwise). The stored
 * role mirrors the membership purely for UI gating; the backend is the
 * authority.
 *
 * PR 4 changed HOW the choice propagates. It used to be localStorage plus
 * `window.location.assign("/")` — switching brokerage reloaded the whole app.
 * That was fine while the two sides were mutually exclusive, but the merged
 * dashboard shows BOTH at once, so the value has to be readable by React
 * (re-render, not reload) and overridable per request.
 *
 * localStorage is still written, for two reasons: the value must survive a
 * refresh, and `ApiClient` reads it through a plain callback that has no access
 * to React context.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface ActingTenant {
  tenantId: string;
  role: string;
  displayName: string;
  brokerageType: string;
}

const KEY = "rp.admin.actingTenant";

export function loadActingTenant(): ActingTenant | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActingTenant>;
    return typeof parsed.tenantId === "string"
      ? (parsed as ActingTenant)
      : null;
  } catch {
    return null;
  }
}

export function saveActingTenant(value: ActingTenant | null): void {
  try {
    if (value) localStorage.setItem(KEY, JSON.stringify(value));
    else localStorage.removeItem(KEY);
  } catch {
    // localStorage unavailable — switching silently no-ops.
  }
}

/**
 * The plain (non-React) accessor `ApiClient` is constructed with. Reads
 * localStorage rather than context because the client is created once at module
 * scope, long before any provider exists.
 */
export function actingTenantId(): string | null {
  return loadActingTenant()?.tenantId ?? null;
}

interface ActingTenantContextValue {
  acting: ActingTenant | null;
  setActing: (value: ActingTenant | null) => void;
}

const ActingTenantContext = createContext<ActingTenantContextValue | null>(
  null,
);

export function ActingTenantProvider({ children }: { children: ReactNode }) {
  // 🔴 The global acting-tenant MODE was retired in PR 5, when the lists began
  // merging both brokerages. Anyone who used the old toggle still has a value
  // in localStorage, and it would otherwise keep pinning their effective role
  // (useRoles) and the nav's brokerage-type gating to whichever side they last
  // selected — while every list showed both. Clear it once, on load.
  //
  // The machinery itself stays: per-row actions and the create/invite pickers
  // still target a specific tenant. It just is not a persistent global mode.
  const [acting, setActingState] = useState<ActingTenant | null>(() => {
    const stale = loadActingTenant();
    if (stale) saveActingTenant(null);
    return null;
  });

  const setActing = useCallback((value: ActingTenant | null) => {
    // Write BEFORE the re-render: ApiClient's callback reads localStorage, and
    // any query that refires as a result of this state change must already see
    // the new value.
    saveActingTenant(value);
    setActingState(value);
  }, []);

  const value = useMemo(() => ({ acting, setActing }), [acting, setActing]);
  return (
    <ActingTenantContext.Provider value={value}>
      {children}
    </ActingTenantContext.Provider>
  );
}

export function useActingTenant(): ActingTenantContextValue {
  const ctx = useContext(ActingTenantContext);
  if (ctx === null) {
    // A screen reading this outside the provider would silently behave as if
    // the user had never switched — a wrong-tenant read, not a cosmetic bug.
    throw new Error("useActingTenant must be used within ActingTenantProvider");
  }
  return ctx;
}
